import useTrackingCore from './core';
import { useAuth } from '@/store';
import { compose } from '@/utils/compose';
import { useCallback } from 'react';
import { useMemo } from 'react';

export default function useRegistrationTracking() {
  const {
    trackEvent,
    trackEventWithCoupon,
    trackEventWithCouponAndPaymentConfig,
    trackLeavePageEvent,
  } = useTrackingCore();

  const auth = useAuth();

  // registration decorators

  const registrationTrackingFunctions = useMemo(() => {
    const withMethod = (trackFn) => {
      return (eventName, data = {}) => {
        trackFn(eventName, {
          ...data,
          validation_method: auth?.auth?.method,
        });
      };
    };

    const withAttempts = (trackFn) => {
      return (eventName, data = {}) => {
        trackFn(eventName, {
          ...data,
          attemp_number: auth?.attemps,
        });
      };
    };

    const trackEventWithMethod = withMethod(trackEvent);
    const trackEventWithAttempts = withAttempts(trackEvent);

    // Registration tracking events
    const trackEventWithAttemptsAndMethod = compose(
      withAttempts,
      withMethod,
    )(trackEvent);

    return {
      checkoutRegistrationViewed: () => {
        trackEventWithCouponAndPaymentConfig('checkout_registration_viewed');
      },

      checkoutVendorLoginClicked: () => {
        trackEventWithCoupon('checkout_vendor_login_clicked');
      },

      // dont use withMethod decorator because it is not available in store when this action is triggered
      checkoutRegistrationSubmitted: (method) => {
        trackEventWithCoupon('checkout_registration_submitted', {
          validation_method: method,
        });
      },

      checkoutRegistrationAbandoned: (reason = 'maunal_exit') => {
        trackLeavePageEvent('checkout_registration_abandoned', {
          reason,
        });
      },

      // OTP

      otpVerificationViewed: () => {
        trackEventWithMethod('otp_verification_viewed', {
          timer_seconds_total: 60,
          timer_seconds_remaining: 60,
        });
      },

      otpTimerExpired: () => {
        trackEvent('otp_timer_expired_options_enabled', {
          timer_seconds_remaining: 0,
        });
      },

      otpResendClicked: (resendMethod) => {
        trackEventWithAttempts('otp_resend_clicked', {
          resend_method: resendMethod,
        });
      },

      otpEditDataClicked: () => {
        trackEvent('otp_edit_data_clicked');
      },

      otpVerifiedSuccess: (secondsRemaining) => {
        trackEventWithAttemptsAndMethod('otp_verified_success', {
          timer_seconds_remaining: secondsRemaining,
        });
      },

      otpVerifiedFailed: (secondsRemaining, failureReason) => {
        trackEventWithAttemptsAndMethod('otp_verified_failed', {
          timer_seconds_remaining: secondsRemaining,
          failure_reason: failureReason,
        });
      },
    };
  }, [
    trackEvent,
    auth?.auth?.method,
    auth?.attemps,
    trackEventWithCouponAndPaymentConfig,
    trackEventWithCoupon,
    trackLeavePageEvent,
  ]);

  return registrationTrackingFunctions;
}
